import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import { Checkbox } from '@mui/material';

import {
  Paper,
  Typography,
  Button,
  IconButton,
  Divider,
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  AppBar,
  Toolbar,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';

import {
  ArrowBackIos,
  Save,
  Search,
  Clear,
  Delete,
  CheckCircle
} from '@mui/icons-material';
import Strings from '../../../../i18n';
import {
  SerialNumberEntry,
  useAddShippingOrderMutation,
  useGetSerialNumberByRangeMutation
} from '../../../../services/manufacture/manufactureService';

export const AddShippingOrderForm = () => {
  const navigate = useNavigate();
  const [soNumber, setSONumber] = useState<string>('');
  const [startSerialNumber, setStartSerialNumber] = useState<string>('');
  const [endSerialNumber, setEndSerialNumber] = useState<string>('');
  const [selectedSerialNumber, setSelectedSerialNumbers] = useState<
    SerialNumberEntry[]
  >([]);
  const [serialNumberResults, setSerialNumberResults] = useState<
    SerialNumberEntry[]
  >([]);

  const [getSerialNumberByRange, { data, isSuccess }] =
    useGetSerialNumberByRangeMutation();

  const [addShippingOrder, { isSuccess: isSuccessAddShippingOrder }] =
    useAddShippingOrderMutation();

  const onSearchSerialNumber = () => {
    if (!startSerialNumber || !endSerialNumber) {
      toast.warning('Please enter both start and end serial numbers');
      return;
    }

    getSerialNumberByRange({
      startSerial: startSerialNumber,
      endSerial: endSerialNumber
    });
  };

  useEffect(() => {
    if (isSuccess && data) {
      const mapped = data.map((value: SerialNumberEntry) => ({
        ...value,
        selected: true
      }));
      setSerialNumberResults(mapped);
    }
  }, [isSuccess, data]);

  const onSaveSelectedSerials = () => {
    const selected = serialNumberResults.filter((item) => item.selected);

    if (selected.length === 0) {
      toast.warning('No serial numbers selected');
      return;
    }

    setSelectedSerialNumbers((prev) => [...prev, ...selected]);
    toast.success(`${selected.length} serial numbers added`);

    setStartSerialNumber('');
    setEndSerialNumber('');
    setSerialNumberResults([]);
  };

  const clearSearch = () => {
    setSerialNumberResults([]);
    setStartSerialNumber('');
    setEndSerialNumber('');
  };

  const removeSerialNumber = (serialToRemove: string) => {
    setSelectedSerialNumbers((prev) =>
      prev.filter((val) => val.serialNumber !== serialToRemove)
    );
  };

  const save = () => {
    if (!soNumber) {
      toast.warning('Please enter SO Number');
      return;
    }

    if (selectedSerialNumber.length === 0) {
      toast.warning('Please select at least one serial number');
      return;
    }

    addShippingOrder({
      soNumber,
      ShippingDate: moment().format('YYYY-MM-DD HH:mm:ss.SSS'),
      SerialNumbers: selectedSerialNumber.map((item) => item.serialNumber)
    });
  };

  useEffect(() => {
    if (isSuccessAddShippingOrder) {
      toast.success('The information was saved successfully.');
      navigate(-1);
    }
  }, [isSuccessAddShippingOrder]);

  const selectAllSerialNumbers = (selected: boolean) => {
    setSerialNumberResults((prev) =>
      prev.map((item) => ({ ...item, selected }))
    );
  };

  return (
    <>
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
            <ArrowBackIos />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, textAlign: 'center' }}>
            {Strings.manufacture.shippingOrder.title}
          </Typography>
          <Tooltip title={Strings.common.save}>
            <IconButton color="secondary" onClick={save}>
              <Save />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            SO Information
          </Typography>
          <TextField
            label="SO Number"
            variant="outlined"
            fullWidth
            value={soNumber}
            onChange={(e) => setSONumber(e.target.value)}
            sx={{ mb: 2 }}
          />
        </Paper>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card elevation={3}>
              <CardHeader
                title="Search Serial Numbers"
                action={
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CheckCircle />}
                    disabled={
                      serialNumberResults.filter((item) => item.selected)
                        .length === 0
                    }
                    onClick={onSaveSelectedSerials}
                  >
                    Confirm Selection
                  </Button>
                }
              />
              <Divider />
              <CardContent>
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      label="Start Serial Number"
                      variant="outlined"
                      fullWidth
                      value={startSerialNumber}
                      onChange={(e) => setStartSerialNumber(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      label="End Serial Number"
                      variant="outlined"
                      fullWidth
                      value={endSerialNumber}
                      onChange={(e) => setEndSerialNumber(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Search">
                        <IconButton
                          color="primary"
                          onClick={onSearchSerialNumber}
                          size="large"
                        >
                          <Search />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Clear">
                        <IconButton
                          color="default"
                          onClick={clearSearch}
                          size="large"
                        >
                          <Clear />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>
                </Grid>

                <Table size="small" sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={
                            serialNumberResults.length > 0 &&
                            serialNumberResults.every((sn) => sn.selected)
                          }
                          indeterminate={
                            serialNumberResults.some((sn) => sn.selected) &&
                            !serialNumberResults.every((sn) => sn.selected)
                          }
                          onChange={(e) =>
                            selectAllSerialNumbers(e.target.checked)
                          }
                        />
                      </TableCell>
                      <TableCell>Serial Number</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>Model</TableCell>
                      <TableCell>Series</TableCell>
                      <TableCell>Manufacture Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {serialNumberResults.map((item, index) => (
                      <TableRow
                        key={index}
                        hover
                        selected={item.selected}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          const updated = [...serialNumberResults];
                          updated[index].selected = !updated[index].selected;
                          setSerialNumberResults(updated);
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={item.selected || false}
                            onChange={(e) => {
                              const updated = [...serialNumberResults];
                              updated[index].selected = e.target.checked;
                              setSerialNumberResults(updated);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell>{item.serialNumber}</TableCell>
                        <TableCell>{item.partNumber?.product ?? '-'}</TableCell>
                        <TableCell>{item.partNumber?.model ?? '-'}</TableCell>
                        <TableCell>{item.partNumber?.series ?? '-'}</TableCell>
                        <TableCell>
                          {moment(item.manufacturingDate).format('YYYY-MM-DD')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card elevation={3}>
              <CardHeader
                title="Selected Serial Numbers"
                subheader={`${selectedSerialNumber.length} items selected`}
              />
              <Divider />
              <CardContent>
                {selectedSerialNumber.length > 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{ maxHeight: 425, overflow: 'auto' }}
                  >
                    <Table size="small" sx={{ minWidth: 650 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Serial Number</TableCell>
                          <TableCell>Product</TableCell>
                          <TableCell>Model</TableCell>
                          <TableCell>Series</TableCell>
                          <TableCell>Manufacture Date</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedSerialNumber.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>{item.serialNumber}</TableCell>
                            <TableCell>
                              {item.partNumber?.product ?? '-'}
                            </TableCell>
                            <TableCell>
                              {item.partNumber?.model ?? '-'}
                            </TableCell>
                            <TableCell>
                              {item.partNumber?.series ?? '-'}
                            </TableCell>
                            <TableCell>
                              {moment(item.manufacturingDate).format(
                                'YYYY-MM-DD'
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                edge="end"
                                color="error"
                                onClick={() =>
                                  removeSerialNumber(item.serialNumber)
                                }
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                ) : (
                  <Box
                    sx={{
                      height: 200,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(0, 0, 0, 0.04)',
                      borderRadius: 1
                    }}
                  >
                    <Typography color="textSecondary">
                      No serial numbers selected
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
